import React, { useContext, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Repeat,
  MessageCircle,
  Contact,
  Network,
  MessagesSquare,
  Users,
  Settings,
  Tag,
  Megaphone,
  CalendarClock,
  Image,
  Users2,
  Building2,
  KanbanSquare,
  MessageSquareText,
  Workflow,
  BarChart3,
  ChevronDown,
  Megaphone as Aviso,
  ListChecks,
  Building,
  Receipt,
  Package,
  Puzzle,
  Shield,
  Sparkles,
  Activity,
  CircleDollarSign,
  BadgePercent,
  Bot,
  Lock,
  LifeBuoy,
  Plug,
  SlidersHorizontal,
  Quote,
  // Seção Redes sociais (Zernio) — Megaphone e BarChart3 já entram acima.
  AtSign,
  FileText,
  MessageSquare,
  Star,
  Target,
  PhoneCall,
} from "lucide-react";

import { cn } from "../lib/utils";
import { lerPreferencia, gravarPreferencia } from "../services/preferencias";
import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { useLigacoes } from "../ligacoes/store";
import { AuthContext } from "../context/Auth/AuthContext";
import useModules from "../hooks/useModules";
import { Can } from "../components/Can";

export function NavItem({
  to,
  icon: Icon,
  label,
  badge,
  onClick,
  collapsed,
  // Recurso fora do plano. Some do menu era pior do que aparecer apagado:
  // quem não vê o item não sabe que ele existe, e não tem o que pedir.
  foraDoPlano,
  exato,
}) {
  return (
    <NavLink
      to={to}
      // `exato` obriga o caminho a bater inteiro. Vale para o item cujo
      // endereço é prefixo de outro ("/zernio" e "/zernio/publicacoes"), que
      // sem isso fica aceso junto com o filho. Não pode ser o padrão: Tickets
      // aponta para "/tickets" e precisa continuar aceso em "/tickets/123".
      end={exato}
      onClick={onClick}
      title={
        foraDoPlano
          ? `${label} — não está no seu plano. Fale com quem administra a conta para liberar.`
          : undefined
      }
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
          "hover:bg-accent hover:text-accent-foreground",
          isActive ? "bg-primary/10 text-primary" : "text-muted-foreground",
          foraDoPlano && "opacity-50"
        )
      }
    >
      <span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
        <Icon className="h-[18px] w-[18px]" />
        {badge && (
          <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground">
            !
          </span>
        )}
      </span>
      {!collapsed && (
        <>
          <span className="truncate">{label}</span>
          {foraDoPlano && <Lock className="ml-auto h-3 w-3 shrink-0" />}
        </>
      )}
    </NavLink>
  );
}

function NavSection({ id, label, collapsed, fechadaPorPadrao, children }) {
  // O "v2" zera a preferência guardada uma vez. Configurações nascia fechada e
  // engolia Conexões, Filas e Usuários; quem já tinha o "fechado" salvo
  // continuaria sem ver mesmo depois da mudança. Depois deste reset, a escolha
  // de cada um volta a valer normalmente.
  const storageKey = `navSection:v2:${id}`;
  const [open, setOpen] = useState(() => {
    const stored = lerPreferencia(storageKey, null);
    // Hoje só a Gestão SaaS nasce fechada: são telas de dono do produto, não
    // do dia a dia de quem atende.
    if (stored === null) return !fechadaPorPadrao;
    return stored === true;
  });

  const toggle = (e) => {
    // não deixa o clique do cabeçalho fechar o drawer mobile
    e.stopPropagation();
    setOpen((prev) => {
      gravarPreferencia(storageKey, !prev);
      return !prev;
    });
  };

  if (collapsed) {
    return (
      <>
        <div className="my-2 h-px bg-border" />
        {children}
      </>
    );
  }

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between rounded-md px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70 transition-colors hover:text-foreground"
      >
        <span>{label}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 transition-transform duration-200",
            !open && "-rotate-90"
          )}
        />
      </button>
      {/* Recuo com guia vertical: mostra de relance onde um grupo começa e
          termina. Sem isso, título e itens ficavam no mesmo alinhamento e a
          lista parecia uma coluna única de 36 linhas soltas. */}
      <div
        className={cn(
          "ml-[18px] mt-0.5 flex flex-col gap-0.5 overflow-hidden border-l border-border/60 pl-1.5 transition-all",
          !open && "hidden"
        )}
      >
        {children}
      </div>
    </div>
  );
}

// O menu como dado, não como JSX.
//
// A barra de cima (TopNav) precisa exatamente dos mesmos itens, nas mesmas
// seções, com as mesmas permissões e os mesmos módulos do plano — só que em
// menus suspensos. Manter duas listas em dois arquivos garante que um dia elas
// divergem: entra uma tela nova na lateral e ela não existe no topo. Por isso
// a fonte é esta função; quem desenha apenas percorre o que ela devolve.
//
// Cada seção: { id, label, fechadaPorPadrao?, itens: [{ to, icon, label,
// badge?, foraDoPlano? }] }. Seção sem `label` é item solto, fora de grupo.
export function useSecoesDoMenu() {
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  // Esconde do menu o que a empresa nao contratou.
  const { temModulo } = useModules();
  // Ligações só existe no menu com o módulo instalado, rodando e contratado.
  const { motorRodando } = useLigacoes();
  const [connectionWarning, setConnectionWarning] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        setConnectionWarning(offlineWhats.length > 0);
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  // `Can` é um componente, mas é função pura sem hook: chamada direta ela
  // devolve o booleano que precisamos aqui. É a mesma regra de sempre
  // (src/rules), sem uma segunda checagem de permissão para manter em dia.
  const podeAdministrar = Can({
    role: user.profile,
    perform: "drawer-admin-items:view",
    yes: () => true,
    no: () => false,
  });

  const secoes = [];

  // Dashboard sozinho no topo: é a tela inicial, não pertence a nenhum grupo.
  secoes.push({
    id: "inicio",
    label: null,
    itens: [{ to: "/", icon: LayoutDashboard, label: "Dashboard" }],
  });

  // Atendimento: a conversa, o contato e o material que a equipe reusa
  // ao responder.
  secoes.push({
    id: "atendimento",
    label: "Atendimento",
    itens: [
      {
        to: "/tickets",
        icon: MessageCircle,
        label: i18n.t("mainDrawer.listItems.tickets"),
      },
      {
        to: "/contacts",
        icon: Contact,
        label: i18n.t("mainDrawer.listItems.contacts"),
      },
      ...(motorRodando
        ? [{ to: "/ligacoes", icon: PhoneCall, label: "Ligações" }]
        : []),
      {
        to: "/quickAnswers",
        icon: MessagesSquare,
        label: i18n.t("mainDrawer.listItems.quickAnswers"),
      },
      {
        to: "/media-library",
        icon: Image,
        label: "Biblioteca de mídias",
        foraDoPlano: !temModulo("mediaLibrary"),
      },
    ],
  });

  // Automação: tudo que fala com o cliente sem ninguém digitar.
  // As mensagens agendadas vieram para cá — elas são envio automático,
  // não material de apoio.
  // Facebook e Instagram estão fora do menu por enquanto: o token da Meta
  // expirou e o app ainda não tem o App Secret para renovar. A tela e a
  // integração continuam no código — é só devolver o item e a rota em
  // routes/index.js quando as credenciais estiverem prontas.
  secoes.push({
    id: "automacao",
    label: "Automação",
    itens: [
      {
        to: "/flows",
        icon: Workflow,
        label: "Fluxo de conversa",
        foraDoPlano: !temModulo("flows"),
      },
      {
        to: "/scheduled-messages",
        icon: CalendarClock,
        label: "Agendamentos",
        foraDoPlano: !temModulo("schedules"),
      },
      { to: "/ai", icon: Sparkles, label: "Talk.Ai" },
    ],
  });

  // Vender: prospecção e disparo em massa moram juntos porque a campanha
  // é a ferramenta de quem trabalha os leads.
  secoes.push({
    id: "vendas",
    label: "Vendas",
    itens: [
      // Leads, funil e tarefas são o mesmo trabalho visto de três ângulos:
      // moram juntos no Kanban, em abas.
      {
        to: "/kanban",
        icon: KanbanSquare,
        label: "Kanban",
        foraDoPlano: !temModulo("crm") && !temModulo("kanban"),
      },
      {
        to: "/campaigns",
        icon: Megaphone,
        label: "Campanhas",
        foraDoPlano: !temModulo("campaigns"),
      },
      {
        to: "/contact-lists",
        icon: ListChecks,
        label: "Listas de Contatos",
        foraDoPlano: !temModulo("campaigns"),
      },
      {
        to: "/campaigns-phrases",
        icon: Quote,
        label: "Fluxo de Campanha",
        foraDoPlano: !temModulo("campaigns"),
      },
      {
        to: "/campaigns-config",
        icon: SlidersHorizontal,
        label: "Config. de campanhas",
        foraDoPlano: !temModulo("campaigns"),
      },
    ],
  });

  // Acompanhar: os números do que já aconteceu.
  secoes.push({
    id: "analise",
    label: "Acompanhamento",
    itens: [
      { to: "/operational-panel", icon: Activity, label: "Painel" },
      {
        to: "/reports",
        icon: BarChart3,
        label: "Relatórios",
        foraDoPlano: !temModulo("reports"),
      },
    ],
  });

  // Equipe: conversa interna, não com o cliente.
  secoes.push({
    id: "equipe",
    label: "Equipe",
    itens: [
      {
        to: "/internal-chat",
        icon: MessageSquareText,
        label: "Chat Interno",
        foraDoPlano: !temModulo("internalChat"),
      },
      { to: "/announcements", icon: Aviso, label: "Informativos" },
      { to: "/helps", icon: LifeBuoy, label: "Ajuda" },
    ],
  });

  // Redes sociais: tudo que passa pela Zernio. Seção própria porque são sete
  // trabalhos diferentes — atender, publicar, responder comentário, responder
  // avaliação — e não variações de uma tela só.
  // Redes sociais é módulo vendido à parte: quem não o contratou vê os itens
  // com cadeado, como acontece com campanhas e fluxos.
  const semZernio = !temModulo("zernio");

  secoes.push({
    id: "zernio",
    label: "Redes sociais",
    fechadaPorPadrao: true,
    itens: [
      // `exato` porque "/zernio" é prefixo das outras seis.
      {
        to: "/zernio",
        icon: Plug,
        label: "Contas",
        exato: true,
        foraDoPlano: semZernio,
      },
      {
        to: "/zernio/publicacoes",
        icon: Megaphone,
        label: "Publicações",
        foraDoPlano: semZernio,
      },
      {
        to: "/zernio/comentarios",
        icon: MessageSquare,
        label: "Comentários",
        foraDoPlano: semZernio,
      },
      {
        to: "/zernio/mencoes",
        icon: AtSign,
        label: "Menções",
        foraDoPlano: semZernio,
      },
      {
        to: "/zernio/avaliacoes",
        icon: Star,
        label: "Avaliações",
        foraDoPlano: semZernio,
      },
      {
        to: "/zernio/analises",
        icon: BarChart3,
        label: "Análises",
        foraDoPlano: semZernio,
      },
      {
        to: "/zernio/anuncios",
        icon: Target,
        label: "Anúncios",
        foraDoPlano: semZernio,
      },
    ],
  });

  // Configurações: o que se ajusta uma vez e esquece. Nasce fechada —
  // são nove linhas que empurravam o dia a dia para fora da tela.
  // Conexões saiu do topo: é configuração, mesmo sendo importante.
  if (podeAdministrar) {
    secoes.push({
      id: "configuracoes",
      label: "Configurações",
      itens: [
        {
          to: "/connections",
          icon: Repeat,
          label: i18n.t("mainDrawer.listItems.connections"),
          badge: connectionWarning,
        },
        {
          to: "/Queues",
          icon: Network,
          label: i18n.t("mainDrawer.listItems.queues"),
        },
        { to: "/tags", icon: Tag, label: i18n.t("mainDrawer.listItems.tags") },
        {
          to: "/users",
          icon: Users,
          label: i18n.t("mainDrawer.listItems.users"),
        },
        { to: "/roles", icon: Shield, label: "Cargos e permissões" },
        { to: "/teams", icon: Users2, label: "Equipes" },
        { to: "/departments", icon: Building2, label: "Departamentos" },
        { to: "/queue-integrations", icon: Bot, label: "Integrações" },
        // Modelos do WhatsApp oficial: sem eles não há campanha nem retomada
        // de conversa passadas 24 h.
        { to: "/modelos-whatsapp", icon: FileText, label: "Modelos WhatsApp" },
        {
          to: "/messages-api",
          icon: Plug,
          label: "Webhooks e API",
          foraDoPlano: !temModulo("webhooks"),
        },
        // Ajustes da plataforma inteira: a rota é isSuperAdmin, então
        // mostrar para o cliente só renderia um 403.
        ...(Number(user.companyId) === 1
          ? [
              {
                to: "/global-config",
                icon: SlidersHorizontal,
                label: "Configurações globais",
              },
            ]
          : []),
        // A empresa operadora já tem o painel financeiro do SaaS; para ela
        // esta tela seria a própria fatura, que não existe.
        ...(Number(user.companyId) !== 1
          ? [
              {
                to: "/financeiro",
                icon: CircleDollarSign,
                label: "Financeiro",
              },
            ]
          : []),
        {
          to: "/Settings",
          icon: Settings,
          label: i18n.t("mainDrawer.listItems.settings"),
        },
      ],
    });
  }

  // Gestão do SaaS: só o admin da empresa operadora (id 1) enxerga.
  if (user.profile === "admin" && Number(user.companyId) === 1) {
    secoes.push({
      id: "saas",
      label: "Gestão SaaS",
      fechadaPorPadrao: true,
      itens: [
        { to: "/saas/finance", icon: CircleDollarSign, label: "Painel financeiro" },
        { to: "/companies", icon: Building, label: "Empresas" },
        { to: "/plans", icon: Package, label: "Planos" },
        { to: "/modules", icon: Puzzle, label: "Módulos" },
        { to: "/saas/commercial", icon: BadgePercent, label: "Comercial" },
        { to: "/invoices", icon: Receipt, label: "Faturas" },
      ],
    });
  }

  return secoes;
}

const MainListItems = ({ drawerClose, collapsed }) => {
  const secoes = useSecoesDoMenu();

  return (
    <nav className="flex flex-col gap-0.5 px-2 pb-6" onClick={drawerClose}>
      {secoes.map((secao) =>
        secao.label ? (
          <NavSection
            key={secao.id}
            id={secao.id}
            label={secao.label}
            collapsed={collapsed}
            fechadaPorPadrao={secao.fechadaPorPadrao}
          >
            {secao.itens.map((item) => (
              <NavItem key={item.to} {...item} collapsed={collapsed} />
            ))}
          </NavSection>
        ) : (
          secao.itens.map((item) => (
            <NavItem key={item.to} {...item} collapsed={collapsed} />
          ))
        )
      )}
    </nav>
  );
};

export default MainListItems;
